import { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';

const EMPTY_CONTACTS = {
  whatsapp_number: '',
  contact_email: '',
  contact_phone: '',
  address: '',
  instagram: '',
};

const digitsOnly = (value = '') => String(value).replace(/\D/g, '');

export const whatsappUrl = (number, message = '') => {
  const raw = digitsOnly(number);
  const normalized = raw.startsWith('00') ? raw.slice(2) : raw;
  if (!normalized) return '';
  const phone = normalized.startsWith('39') ? normalized : `39${normalized}`;
  return `https://wa.me/${phone}${message ? `?text=${encodeURIComponent(message)}` : ''}`;
};

const phoneUrl = (number = '') => {
  const raw = digitsOnly(number);
  if (!raw) return '';
  const normalized = raw.startsWith('00') ? raw.slice(2) : raw;
  return `tel:+${normalized.startsWith('39') ? normalized : `39${normalized}`}`;
};

const displayPhone = (number = '') => {
  const raw = String(number).trim();
  if (!raw) return '';
  const digits = digitsOnly(raw);
  if (digits.length === 10 && !digits.startsWith('39')) return `+39 ${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  return raw.startsWith('+') || raw.startsWith('00') ? raw : `+${raw}`;
};

const instagramProfile = (value = '') => {
  const raw = String(value).trim();
  if (!raw) return { href: '', label: '' };
  if (/^https?:\/\//i.test(raw)) {
    const handle = raw.replace(/\?.*$/, '').replace(/\/$/, '').split('/').pop();
    return { href: raw, label: handle ? `@${handle.replace(/^@/, '')}` : raw };
  }
  const handle = raw.replace(/^@/, '').replace(/^instagram\.com\//i, '').replace(/^www\.instagram\.com\//i, '').replace(/\/$/, '');
  return { href: `https://www.instagram.com/${handle}/`, label: `@${handle}` };
};

export const replaceLegacyContactText = (text, contacts) => {
  if (!text) return text;
  return text
    .replace(/assistenza@harizigioielli\.com/gi, contacts.contactEmail || '')
    .replace(/\+39\s*345\s*350\s*5333/g, contacts.publicPhoneDisplay || '');
};

export default function useContactSettings() {
  const [settings, setSettings] = useState(EMPTY_CONTACTS);

  useEffect(() => {
    let active = true;
    api.get('/site-settings')
      .then(({ data }) => {
        if (active) setSettings({ ...EMPTY_CONTACTS, ...data });
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  return useMemo(() => {
    const whatsappNumber = settings.whatsapp_number || '';
    const contactPhone = settings.contact_phone || '';
    const instagram = instagramProfile(settings.instagram);
    return {
      whatsappNumber,
      whatsappDisplay: displayPhone(whatsappNumber),
      whatsappHref: (message = '') => whatsappUrl(whatsappNumber, message),
      contactEmail: String(settings.contact_email || '').trim(),
      contactPhone: String(contactPhone).trim(),
      contactPhoneDisplay: displayPhone(contactPhone),
      contactPhoneHref: phoneUrl(contactPhone),
      publicPhoneDisplay: displayPhone(contactPhone || whatsappNumber),
      address: String(settings.address || '').trim(),
      instagramHref: instagram.href,
      instagramLabel: instagram.label,
    };
  }, [settings]);
}